import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import type { MfeProps } from "./types";

const connectionString = process.env.APPINSIGHTS_CONNECTION_STRING;

let ai: ApplicationInsights | null = null;

if (connectionString) {
  ai = new ApplicationInsights({
    config: {
      connectionString,
      // The shell owns the URL, so route tracking is accurate here
      enableAutoRouteTracking: true,
    },
  });
  ai.loadAppInsights();
  // Distinguish shell telemetry when the App Insights resource is shared with backend services
  ai.addTelemetryInitializer((item) => {
    item.tags = item.tags ?? {};
    item.tags["ai.cloud.role"] = "shell";
  });
}

/** Associates subsequent telemetry with the signed-in user. Call once MSAL login resolves. */
export function setAuthenticatedUser(username: string, localAccountId: string): void {
  ai?.setAuthenticatedUserContext(username, localAccountId, true);
}

/** Fires a shell-owned event (e.g. remote.mounted). No-op when telemetry is not configured. */
export function trackShellEvent(name: string, properties?: Record<string, unknown>): void {
  try {
    ai?.trackEvent({ name, properties });
  } catch {
    // fire-and-forget
  }
}

/**
 * Returns the telemetry prop for a remote's mount call, with every event
 * stamped with the remote's name so attribution is automatic and remotes
 * can't spoof each other. Returns undefined when telemetry is not configured —
 * the MfeProps contract makes the field optional and remotes handle absence.
 */
export function telemetryFor(remoteName: string): MfeProps["telemetry"] {
  if (!ai) return undefined;
  return {
    trackEvent: (name, properties) => {
      try {
        ai?.trackEvent({ name, properties: { ...properties, remote: remoteName } });
      } catch {
        // fire-and-forget: must never throw into a remote
      }
    },
  };
}
